import axios from 'axios';
import { LoginResponse, UserData } from '../types/Api';
import { API_BASE_URL } from '../constants/api';
import { ActionType } from '../types/ServiceRequest';


const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
  withCredentials: true
});

// API yanıtlarını ve oturum durumunu kontrol eden interceptor
api.interceptors.response.use(
  response => {
    // 1. Tamamen boş yanıt durumu (undefined/null) veya boş obje
    if (!response.data || 
        (typeof response.data === 'object' && Object.keys(response.data).length === 0)) {
      // Oturum bilgisini temizle
      localStorage.removeItem('auth_token');
      // Login sayfasına yönlendir
      window.location.href = '/login';
      return Promise.reject(new Error('Oturum süresi doldu'));
    }
    
    // 2. Beklenen yapıda bir yanıt mı kontrol et
    // LoginResponse içindeki success özellliği varsa ve false ise
    if (response.data && 'success' in response.data && response.data.success === false) {
      if (response.data.msg?.toLowerCase().includes('oturum')) {
        // Oturum hatası mesajları için login sayfasına yönlendir
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return Promise.reject(new Error(response.data.msg || 'Oturum süresi doldu'));
      }
    }
    
    // 3. SerialNumberResponse, PartGroupsResponse gibi listeleyici yanıtların
    // total değeri 0 olsa bile, bu normal bir durumdur, oturum hatası değil
    
    return response;
  },
  error => {
    // Hata durumlarında
    if (error.response?.status === 401) {
      // Oturum sonlandığında login sayfasına yönlendir
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    } else if (!error.response) {
      // Ağ hatası durumunda da oturumun kontrolü için
      // Sunucudan yanıt alınamadığı durumlarda oturum kontrolü yapmak
      // için isAuthenticated fonksiyonunu kullanarak kontrol edebiliriz
      if (!isAuthenticated()) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Oturum durumunu yerel olarak kontrol eden yardımcı fonksiyon
export const isAuthenticated = (): boolean => {
  // localStorage'da veya sessionStorage'da bir token veya oturum bilgisi var mı kontrol et
  return localStorage.getItem('auth_token') !== null || 
         sessionStorage.getItem('auth_token') !== null ||
         document.cookie.includes('auth_token=');
};

export const login = async (username: string, password: string): Promise<{loginResponse: LoginResponse, userData: UserData | null}> => {
  const formData = new URLSearchParams();
  formData.append('KullaniciAdi', username);
  formData.append('Parola', password);
  formData.append('FirmaKodu', 'bolat');

  const response = await api.post<LoginResponse>('/applogin', formData);
  
  let userData = null;
  
  // Başarılı giriş sonrası token'ı saklayalım ve kullanıcı verilerini alalım
  if (response.data && response.data.success) {
    localStorage.setItem('auth_token', 'true');
    
    try {
      // Hemen kullanıcı verilerini alalım, ama direkt API'den değil,
      // login yanıtında user bilgisi varsa onu kullanalım
      if ('userData' in response.data && response.data.userData) {
        // API yanıtında userData varsa, doğrudan kullan
        userData = response.data.userData as UserData;
      } else {
        // API yanıtında userData yoksa, ayrı bir istek yap
        userData = await getUserData();
      }
    } catch (error) {
      console.error('Kullanıcı bilgileri alınamadı:', error);
    }
  }
  
  return {
    loginResponse: response.data,
    userData
  };
};

export const logout = async (): Promise<void> => {
  await api.get('/applogin');
  // Logout sonrası token'ı temizleyelim
  localStorage.removeItem('auth_token');
};

export const getUserData = async (): Promise<UserData> => {
  // Eğer kimlik doğrulama yoksa boş bir istek göndermek yerine hata fırlat
  if (!isAuthenticated()) {
    return Promise.reject(new Error('Oturum açılmamış'));
  }
  
  const response = await api.get<UserData>('/user/initialization');
  return response.data;
};

export const updatePassword = async (newPassword: string): Promise<{success: boolean, error?: string}> => {
  try {
    const formData = new URLSearchParams();
    formData.append('isTree', 'false');
    formData.append('limitless', 'false');
    formData.append('storeId', `store-${Date.now()}`);
    formData.append('model', 'Cariler');
    formData.append('fields', 'KullaniciAdi,Ad,Il,Ilce,Ulke,IlAdi,IlceAdi,UlkeAdi,Adres,PostaKodu,Telefon1,Telefon2,Faks,EPosta,WebSitesi,Id');
    formData.append('writemodel', 'Cariler');
    formData.append('fromview', 'true');
    formData.append('masterfield', 'Id');
    
    // Kullanıcı verilerini al
    const userData = await getUserData();
    if (!userData || !userData.User) {
      return { success: false, error: 'Kullanıcı bilgileri alınamadı' };
    }
    
    formData.append('masterid', userData.User.toString());

    // Sadece parola alanını içeren data
    const dataObject = {
      Parola: newPassword,
      Id: userData.User.toString(),
      Tracker: Date.now().toString()
    };

    formData.append('data', JSON.stringify(dataObject));

    const response = await api.post('/data/update', formData);

    if (response.data && response.data.success) {
      return { success: true };
    } else {
      // Server'dan gelen hata mesajını kontrol et
      const errorMessage = response.data?.msg || response.data?.error || 'Parola güncellenemedi';
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    console.error('Parola güncelleme hatası:', error);
    return { success: false, error: 'Parola güncelleme sırasında bir hata oluştu' };
  }
};

// Kullanıcı bilgilerini getir
export const getUserInfo = async (userId: number): Promise<{success: boolean, data?: any, error?: string}> => {
  try {
    const formData = new URLSearchParams();
    formData.append('isTree', 'false');
    formData.append('limitless', 'false');
    formData.append('storeId', `store-${Date.now()}`);
    formData.append('model', 'Cariler');
    formData.append('fields', 'KullaniciAdi,Ad,Il,Ilce,Ulke,IlAdi,IlceAdi,UlkeAdi,Adres,PostaKodu,Telefon1,Telefon2,Faks,EPosta,WebSitesi,Id');
    formData.append('writemodel', 'Cariler');
    formData.append('fromview', 'true');
    formData.append('masterfield', 'Id');
    formData.append('masterid', userId.toString());
    formData.append('page', '1');
    formData.append('start', '0');
    formData.append('limit', '50');
    formData.append('sort', 'Id');
    formData.append('dir', 'ASC');

    const response = await api.post('/data/read', formData);

    if (response.data && response.data.total > 0) {
      return { success: true, data: response.data.data[0] };
    } else {
      return { success: false, error: 'Kullanıcı bilgileri bulunamadı' };
    }
  } catch (error) {
    console.error('Kullanıcı bilgileri alma hatası:', error);
    return { success: false, error: 'Kullanıcı bilgileri alınırken bir hata oluştu' };
  }
};

// Kullanıcı bilgilerini güncelle
export const updateUserInfo = async (userData: any, userId: number): Promise<{success: boolean, error?: string}> => {
  try {
    const formData = new URLSearchParams();
    formData.append('isTree', 'false');
    formData.append('limitless', 'false');
    formData.append('storeId', `store-${Date.now()}`);
    formData.append('model', 'Cariler');
    formData.append('fields', 'KullaniciAdi,Ad,Il,Ilce,Ulke,IlAdi,IlceAdi,UlkeAdi,Adres,PostaKodu,Telefon1,Telefon2,Faks,EPosta,WebSitesi,Id');
    formData.append('writemodel', 'Cariler');
    formData.append('fromview', 'true');
    formData.append('masterfield', 'Id');
    formData.append('masterid', userId.toString());

    // Kullanıcı bilgilerini içeren data
    const dataObject = {
      ...userData,
      Id: userId.toString(),
      Tracker: Date.now().toString()
    };

    formData.append('data', JSON.stringify(dataObject));

    const response = await api.post('/data/update', formData);

    if (response.data && response.data.success) {
      return { success: true };
    } else {
      // Server'dan gelen hata mesajını kontrol et
      const errorMessage = response.data?.msg || response.data?.error || 'Kullanıcı bilgileri güncellenemedi';
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    return { success: false, error: 'Kullanıcı bilgileri güncelleme sırasında bir hata oluştu' };
  }
};

// Alıcı bilgilerini güncelleme
export const updateBuyerInfo = async (buyerInfo: any, productId: string): Promise<{success: boolean, data?: any, error?: string}> => {
  try {
    const formData = new URLSearchParams();
    
    // Sabit parametreler
    formData.append('ignoreclient', 'true');
    formData.append('isTree', 'false');
    formData.append('limitless', 'false');
    formData.append('storeId', `store-${Date.now()}`);
    formData.append('model', 'Kuponlar');
    formData.append('fields', 'SonIslem,Tip,MotorMu,KategoriAdi,MarkaAdi,Model,ModelAdi,RenkAdi,Sasi,Motor,MontajBedeli,BakimYapilabilir,TalepGirilebilir,CariAdi,MontajYapildiMi,TalepVarMi,SaticiTelefon,SaticiAdres,SaticiEPosta,SaticiFaturaNo,SaticiBelgeNo,SaticiTeslimYeri,SaticiTeslimEdilen,Ulke,SaticiIl,SaticiIlAdi,SaticiIlce,SaticiIlceAdi,AliciTelefon,AliciAdres,AliciEPosta,AliciFaturaNo,AliciBelgeNo,AliciTeslimYeri,AliciTeslimEdilen,IslemAdi,GarantiDurumu,GarantiDurumuAciklamasi,AktifMi,GarantiDevamEdiyorMu,SaticiFaturaTarihi,SaticiTeslimTarihi,AliciFaturaTarihi,AliciTeslimTarihi,GarantiBitisTarihi,IslemTarihi,Id');
    formData.append('writemodel', '');
    formData.append('fromview', 'true');
    formData.append('masterfield', '');
    formData.append('masterid', '0');
    
    // Tarih formatını dd.mm.yyyy 00:00:00 şeklinde hazırla
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year} 00:00:00`;
    };
    
    const currentDate = new Date();
    const formattedCurrentDate = `${String(currentDate.getDate()).padStart(2, '0')}.${String(currentDate.getMonth() + 1).padStart(2, '0')}.${currentDate.getFullYear()} ${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}:00`;
    
    // Data objesi
    const data = {
      SonIslem: ActionType.WAITING_FOR_APPROVAL,
      AliciTelefon: buyerInfo.telefon || '',
      AliciAdres: buyerInfo.adres || '',
      AliciEPosta: buyerInfo.email || '',
      AliciFaturaNo: buyerInfo.faturaNo || '',
      AliciBelgeNo: buyerInfo.belgeNo || '',
      AliciTeslimYeri: buyerInfo.teslimYeri || '',
      AliciTeslimEdilen: buyerInfo.teslimEdilen || '',
      AliciIl: buyerInfo.il || null,
      AliciIlce: buyerInfo.ilce || null,
      GarantiDurumu: 1,
      Notlar: '',
      GarantiNotlari: '',
      AktifMi: true,
      AliciFaturaTarihi: formatDate(buyerInfo.faturaTarihi),
      AliciTeslimTarihi: formatDate(buyerInfo.teslimTarihi),
      IslemTarihi: formattedCurrentDate,
      Id: productId,
      Tracker: Date.now().toString()
    };
    
    formData.append('data', JSON.stringify(data));
    
    const response = await api.post('/data/update', formData);
    
    if (response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      return {
        success: false,
        error: response.data?.msg || 'Kayıt işlemi başarısız'
      };
    }
  } catch (error: any) {
    console.error('Alıcı bilgileri güncelleme hatası:', error);
    return {
      success: false,
      error: error.message || 'Kayıt işlemi sırasında bir hata oluştu'
    };
  }
};

// Seri numarası kontrolü - durumu kontrol et
export const checkSerialNumber = async (serialNumber: string): Promise<{success: boolean, data?: any, error?: string, isWaiting?: boolean}> => {
  try {
    const formData = new URLSearchParams();
    
    // Sabit parametreler
    formData.append('ignoreclient', 'true');
    formData.append('isTree', 'false');
    formData.append('limitless', 'false');
    formData.append('storeId', `store-${Date.now()}`);
    formData.append('model', 'Kuponlar');
    formData.append('fields', 'SonIslem,Tip,MotorMu,KategoriAdi,MarkaAdi,Model,ModelAdi,RenkAdi,Sasi,Motor,MontajBedeli,BakimYapilabilir,TalepGirilebilir,CariAdi,MontajYapildiMi,TalepVarMi,SaticiTelefon,SaticiAdres,SaticiEPosta,SaticiFaturaNo,SaticiBelgeNo,SaticiTeslimYeri,SaticiTeslimEdilen,Ulke,SaticiIl,SaticiIlAdi,SaticiIlce,SaticiIlceAdi,AliciTelefon,AliciAdres,AliciEPosta,AliciFaturaNo,AliciBelgeNo,AliciTeslimYeri,AliciTeslimEdilen,IslemAdi,GarantiDurumu,GarantiDurumuAciklamasi,AktifMi,GarantiDevamEdiyorMu,SaticiFaturaTarihi,SaticiTeslimTarihi,AliciFaturaTarihi,AliciTeslimTarihi,GarantiBitisTarihi,IslemTarihi,Id');
    formData.append('writemodel', '');
    formData.append('fromview', 'true');
    formData.append('masterfield', '');
    formData.append('masterid', '0');
    
    // Seri numarası filtresini hazırla
    const filter = JSON.stringify([
      {
        "type": "string",
        "value": serialNumber,
        "field": "Sasi"
      }
    ]);
    formData.append('filter', filter);
    
    // Prefilter bilgisini ekle
    const prefilter = JSON.stringify([
      {"type":"boolean","field":"TalepGirilebilir","value":true},
      {"type":"boolean","field":"AktifMi","value":true}
    ]);
    formData.append('prefilter', prefilter);
    
    formData.append('page', '1');
    formData.append('start', '0');
    formData.append('limit', '50');
    formData.append('sort', 'Sasi');
    formData.append('dir', 'ASC');
    
    const response = await api.post('/data/read', formData);
    
    if (response.data && response.data.total > 0) {
      const productData = response.data.data[0];
      const isWaiting = productData.SonIslem === ActionType.CREATED || productData.SonIslem === ActionType.WAITING_FOR_APPROVAL;
      
      return {
        success: true,
        data: productData,
        isWaiting: isWaiting
      };
    } else {
      return {
        success: false,
        error: 'Seri numarası bulunamadı'
      };
    }
  } catch (error: any) {
    console.error('Seri numarası kontrolü hatası:', error);
    return {
      success: false,
      error: error.message || 'Seri numarası kontrol edilirken bir hata oluştu'
    };
  }
};

// Bakım için seri numarası kontrolü
export const checkSerialNumberForMaintenance = async (serialNumber: string): Promise<{success: boolean, data?: any, error?: string}> => {
  try {
    const formData = new URLSearchParams();
    
    // Sabit parametreler
    formData.append('ignoreclient', 'true');
    formData.append('isTree', 'false');
    formData.append('limitless', 'false');
    formData.append('storeId', `store-${Date.now()}`);
    formData.append('model', 'Kuponlar');
    formData.append('fields', 'SonIslem,Tip,MotorMu,KategoriAdi,MarkaAdi,Model,ModelAdi,RenkAdi,Sasi,Motor,MontajBedeli,BakimYapilabilir,TalepGirilebilir,CariAdi,MontajYapildiMi,TalepVarMi,SaticiTelefon,SaticiAdres,SaticiEPosta,SaticiFaturaNo,SaticiBelgeNo,SaticiTeslimYeri,SaticiTeslimEdilen,Ulke,SaticiIl,SaticiIlAdi,SaticiIlce,SaticiIlceAdi,AliciTelefon,AliciAdres,AliciEPosta,AliciFaturaNo,AliciBelgeNo,AliciTeslimYeri,AliciTeslimEdilen,IslemAdi,GarantiDurumu,GarantiDurumuAciklamasi,AktifMi,GarantiDevamEdiyorMu,SaticiFaturaTarihi,SaticiTeslimTarihi,AliciFaturaTarihi,AliciTeslimTarihi,GarantiBitisTarihi,IslemTarihi,Id');
    formData.append('writemodel', '');
    formData.append('fromview', 'true');
    formData.append('masterfield', '');
    formData.append('masterid', '0');
    
    // Seri numarası filtresini hazırla
    const filter = JSON.stringify([
      {
        "type": "string",
        "value": serialNumber,
        "field": "Sasi"
      }
    ]);
    formData.append('filter', filter);
    
    // Bakım için prefilter bilgisini ekle
    const prefilter = JSON.stringify([
      {"type":"boolean","field":"BakimYapilabilir","value":true}
    ]);
    formData.append('prefilter', prefilter);
    
    formData.append('page', '1');
    formData.append('start', '0');
    formData.append('limit', '50');
    formData.append('sort', 'Sasi');
    formData.append('dir', 'ASC');
    
    const response = await api.post('/data/read', formData);
    
    if (response.data && response.data.total > 0) {
      const productData = response.data.data[0];
      
      return {
        success: true,
        data: productData
      };
    } else {
      return {
        success: false,
        error: 'Bakım yapılabilir seri numarası bulunamadı'
      };
    }
  } catch (error: any) {
    console.error('Bakım seri numarası kontrolü hatası:', error);
    return {
      success: false,
      error: error.message || 'Seri numarası kontrol edilirken bir hata oluştu'
    };
  }
};

// Servis notlarını veya diğer verileri çekme (genel endpoint)
export const getNotesData = async (store: string, model: string, id: string): Promise<{success: boolean, data?: any, error?: string}> => {
  try {
    const formData = new URLSearchParams();
    formData.append('store', store);
    formData.append('model', model);
    formData.append('id', id);
    formData.append('limitless', 'true');
    
    const response = await api.post('/json', formData);
    
    if (response.data && response.data.data) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      return {
        success: false,
        error: 'Veri bulunamadı'
      };
    }
  } catch (error: any) {
    console.error('Veri çekme hatası:', error);
    return {
      success: false,
      error: error.message || 'Veri çekme sırasında bir hata oluştu'
    };
  }
};

export const getRecordFiles = async (recordId: string, model: string = 'Talepler'): Promise<{success: boolean, data?: any, error?: string}> => {
  try {
    const formData = new URLSearchParams();
    formData.append('isTree', 'false');
    formData.append('limitless', 'false');
    formData.append('storeId', `store-${Date.now()}`);
    formData.append('model', 'Dosyalar');
    formData.append('fields', 'Model,Kayit,Rumuz,Ad,Tip,Boyut,Id');
    formData.append('writemodel', '');
    formData.append('fromview', 'false');
    formData.append('masterfield', '');
    formData.append('masterid', '0');
    formData.append('prefilter', JSON.stringify([
      {
        "type": "string",
        "field": "Model",
        "value": model
      },
      {
        "type": "numeric",
        "field": "Kayit",
        "value": parseInt(recordId)
      }
    ]));
    formData.append('page', '1');
    formData.append('start', '0');
    formData.append('limit', '50');
    formData.append('sort', 'Id');
    formData.append('dir', 'ASC');
    
    const response = await api.post('/data/read', formData);
    
    if (response.data && response.data.data) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      return {
        success: false,
        error: 'Dosya bulunamadı'
      };
    }
  } catch (error: any) {
    console.error('Dosya çekme hatası:', error);
    return {
      success: false,
      error: error.message || 'Dosya çekme sırasında bir hata oluştu'
    };
  }
};


export default api; 